import { Provider, Root, Trigger, Portal, Content, Arrow } from '@radix-ui/react-tooltip';
import Image from 'next/image';
import React, { JSX, useContext } from 'react';

import { Badge } from '@videoblocks/design-system';

import { StatsigExperimentContext } from '../../context/NavSearchContext';
import { getConversationalSearchUnavailableMessage } from './conversationalSearchMessaging';

const AICtaTooltipContent = ({ enabled }: { enabled: boolean }): JSX.Element => (
    <div className="flex items-center gap-3 p-3 pl-4">
        <Image
            height={170}
            width={170}
            src="/assets/images/conversational-tooltip.webp"
            alt="Conversational search preview"
            className="shrink-0 rounded border border-white object-cover"
        />
        <div className="flex flex-col">
            <div className="flex items-center gap-2.5 pb-1">
                <span className="text-base font-semibold text-white">
                    {enabled ? 'Conversational Search Enabled!' : 'Conversational Search'}
                </span>
                {!enabled && (
                    <Badge color="neutral" size="small" shape="square">
                        NEW
                    </Badge>
                )}
            </div>
            <p className="text-sm leading-5 text-gray-400">
                Find content by describing your needs with natural language and refine your search through chat.
            </p>
        </div>
    </div>
);

// Shown when the auto-on-hover feature is enabled: a stripped-down, text-only
// hint with no image, title, or badge — centered white copy.
const AutoOnHintTooltipContent = (): JSX.Element => (
    <div className="flex items-center justify-center p-3 w-64">
        <p className="text-center text-base font-semibold leading-7 text-white">
            Search and refine using natural language
        </p>
    </div>
);

const DisabledForContentTypeTooltipContent = (): JSX.Element => {
    const { showConversationalSearchAudio } = useContext(StatsigExperimentContext);
    return (
        <div className="flex items-center gap-3 p-3 pl-4">
            <div className="flex flex-col">
                <div className="flex items-center gap-2.5 pb-1">
                    <span className="text-base text-white">
                        {getConversationalSearchUnavailableMessage(showConversationalSearchAudio ?? false)}
                    </span>
                </div>
            </div>
        </div>
    );
};

const StandardSearchTooltipContent = ({ onDismiss }: { onDismiss: () => void }): JSX.Element => (
    <div className="flex items-center gap-3 p-3 pl-4 w-48">
        <div className="flex flex-col">
            <div className="flex items-center gap-2.5 pb-1">
                <span className="text-base font-semibold text-white">Standard Search</span>
            </div>
            <p className="text-sm leading-5 text-gray-400">Switch back to standard search anytime.</p>
            <div className="mt-2 flex items-center gap-2.5">
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-sm font-normal text-white hover:underline cursor-pointer"
                >
                    Switch Back
                </button>
            </div>
        </div>
    </div>
);

interface ConversationalSearchTooltipProps {
    disabledForContentType?: boolean;
    enabled: boolean;
    // True when the auto-on-hover feature is configured on (delay > 0); surfaces the text-only hint.
    autoOnEnabled?: boolean;
    setEnabled: (enabled: boolean) => void;
    trigger: JSX.Element;
}

export default function ConversationalSearchTooltip({
    disabledForContentType,
    enabled,
    autoOnEnabled,
    setEnabled,
    trigger,
}: ConversationalSearchTooltipProps): JSX.Element {
    // Snapshot `enabled` when the tooltip opens so toggling mid-hover doesn't swap content.
    const [enabledAtHover, setEnabledAtHover] = React.useState(enabled);

    // We need to instantiate a new Provider here to get around BuilderBlock's use of a separate React tree.
    // Builder-rendered tooltips can't access the state/context of the main app (Providers live in the root layout)
    return (
        <Provider>
            <Root
                delayDuration={0}
                onOpenChange={(open) => {
                    if (open) setEnabledAtHover(enabled);
                }}
            >
                <Trigger asChild>{trigger}</Trigger>
                <Portal>
                    <Content side="top" sideOffset={8} className="z-50 max-w-md rounded-xl bg-gray-950 shadow-lg">
                        {disabledForContentType ? (
                            <DisabledForContentTypeTooltipContent />
                        ) : enabledAtHover ? (
                            <StandardSearchTooltipContent onDismiss={() => setEnabled(false)} />
                        ) : autoOnEnabled ? (
                            <AutoOnHintTooltipContent />
                        ) : (
                            <AICtaTooltipContent enabled={enabled} />
                        )}
                        <Arrow className="fill-gray-950" style={{ clipPath: 'inset(0 -10px -10px -10px)' }} />
                    </Content>
                </Portal>
            </Root>
        </Provider>
    );
}
